import { useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  AndroidOutlined,
  CheckCircleOutlined,
  CloudServerOutlined,
  EditOutlined,
  EyeOutlined,
  KeyOutlined,
  PlusOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  StopOutlined,
  WindowsOutlined,
} from '@ant-design/icons'
import { AndroidEnforceDrawer } from '@/components/AndroidEnforceDrawer'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import type { AdminRecordAction } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'
import type {
  AndroidEnforcePreflightArgs,
  AndroidEnforcePreflightResult,
  ClientCapabilityUpdateArgs,
} from '@/lib/android-enforce'
import {
  APP_ID_PATTERN,
  BUILD_KEY_ID_PATTERNS,
  BUILD_KEY_PLATFORMS,
  BUILD_KEY_PLATFORM_COPY,
  BUILD_SECRET_PATTERN,
  CHANNEL_PATTERN,
  buildKeyPlatform,
} from '@/lib/client-build-keys'
import type { BuildKeyPlatform } from '@/lib/client-build-keys'
import { supabase } from '@/lib/supabase'
import { formatCST } from '@/lib/time'

type JsonRecord = Record<string, unknown>
type RpcResult = { ok: boolean; error?: string; id?: number; revision?: number }
type ListResult<T> = RpcResult & { rows: T[]; total: number }

type AppRelease = {
  id: number
  version_code: number
  version_name: string
  min_sdk: number | null
  release_notes: string
  update_message: string | null
  apk_url: string
  apk_sha256: string
  apk_md5: string | null
  apk_size_bytes: number | null
  splits_data: Array<{ name: string; size_bytes: number }>
  first_published_at: string | null
  revision: number
  created_at: string
  updated_at: string
}

type AppChannel = {
  slug: string
  assignment_mode: 'client' | 'device'
  priority: number
  is_active: boolean
  active_deployment_count?: number
}

type AppDeployment = {
  id: number
  channel_slug: string
  release_id: number
  min_supported_code: number | null
  staged_rollout_percent: number
  rollout_seed: string
  is_active: boolean
  published_at: string | null
  revision: number
  updated_at: string
  channel: AppChannel
  release: AppRelease
}

type AppMember = {
  id: number
  device_hint: string
  channel_slug: string
  note: string | null
  is_active: boolean
  updated_at: string
}

type ClientBuildKey = {
  key_id: string
  app_id: string
  version_code: number
  version_name: string
  channel: string
  signing_cert_sha256: string
  status: 'active' | 'revoked' | 'retired'
  not_before: string
  expires_at: string | null
  revoked_at: string | null
  last_matched_at: string | null
  revision: number
  created_at: string
  updated_at: string
}

type CapabilityMode = 'disabled' | 'observe' | 'soft' | 'enforce'

type ClientCapabilityPolicy = {
  capability: string
  mode: CapabilityMode
  soft_min_version_code: number | null
  hard_min_version_code: number | null
  grace_deadline: string | null
  emergency_grace_seconds: number
  revision: number
  updated_at: string
}

type ClientTrustAudit = {
  id: number
  action: string
  actor_kind: 'admin_user' | 'github_actions'
  target_type: string
  target_id: string
  reason: string
  request_id: string | null
  occurred_at: string
  after_value: JsonRecord | null
}

type CopyValues = {
  release_notes: string
  update_message?: string
  reason?: string
}

type DeploymentPolicyValues = {
  staged_rollout_percent: number
  min_supported_code?: number | null
  reason: string
}

type CapabilityValues = {
  mode: Exclude<CapabilityMode, 'enforce'>
  soft_min_version_code?: number | null
  hard_min_version_code?: number | null
  emergency_grace_seconds: number
  reason: string
}

type BuildKeyValues = {
  platform: BuildKeyPlatform
  key_id: string
  app_id: string
  version_code: number
  version_name: string
  channel: string
  signing_cert_sha256: string
  build_secret: string
  not_before?: string
  expires_at?: string
  reason: string
  request_id?: string
}

type BuildKeySubmission = Omit<BuildKeyValues, 'build_secret' | 'platform'>

type TabKey = 'runtime' | 'releases' | 'trust' | 'channels'
type DeploymentFilter = 'active' | 'all'
type KeyFilter = 'active' | 'all' | 'revoked'
type KeyPlatformFilter = 'all' | BuildKeyPlatform

const PAGE_SIZE = 200
/** 手机卡片列表每页条数（桌面表格仍是 20 / 页） */
const MOBILE_PAGE_SIZE = 20

export const Route = createFileRoute('/_authed/app-versions')({
  component: AppVersionsPage,
})

function AppVersionsPage() {
  const { message } = App.useApp()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState<TabKey>('runtime')
  const [deploymentFilter, setDeploymentFilter] =
    useState<DeploymentFilter>('active')
  const [keyFilter, setKeyFilter] = useState<KeyFilter>('active')
  const [keyPlatformFilter, setKeyPlatformFilter] =
    useState<KeyPlatformFilter>('all')
  const [copyTarget, setCopyTarget] = useState<AppRelease | null>(null)
  const [detail, setDetail] = useState<AppDeployment | AppRelease | null>(null)
  const [policyTarget, setPolicyTarget] = useState<AppDeployment | null>(null)
  const [capabilityTarget, setCapabilityTarget] =
    useState<ClientCapabilityPolicy | null>(null)
  const [enforceTarget, setEnforceTarget] =
    useState<ClientCapabilityPolicy | null>(null)
  const [disableCapabilityTarget, setDisableCapabilityTarget] =
    useState<ClientCapabilityPolicy | null>(null)
  const [deactivateTarget, setDeactivateTarget] =
    useState<AppDeployment | null>(null)
  const [buildKeyDrawerOpen, setBuildKeyDrawerOpen] = useState(false)
  const [revokeKeyTarget, setRevokeKeyTarget] = useState<ClientBuildKey | null>(
    null,
  )
  const buildSecretRef = useRef('')
  const [channelDrawerOpen, setChannelDrawerOpen] = useState(false)
  const [memberDrawerOpen, setMemberDrawerOpen] = useState(false)
  const isMobile = useIsMobile()
  const [deploymentPage, setDeploymentPage] = useState(1)
  const [releasePage, setReleasePage] = useState(1)
  const [keyPage, setKeyPage] = useState(1)

  const releasesQuery = useQuery({
    queryKey: ['admin-app-releases'],
    queryFn: () => adminList<AppRelease>('list_app_releases'),
  })
  const channelsQuery = useQuery({
    queryKey: ['admin-app-update-channels'],
    queryFn: () => adminList<AppChannel>('list_app_update_channels'),
  })
  const deploymentsQuery = useQuery({
    queryKey: ['admin-app-update-deployments'],
    queryFn: () => adminList<AppDeployment>('list_app_update_deployments'),
  })
  const membersQuery = useQuery({
    queryKey: ['admin-app-channel-members'],
    queryFn: () => adminList<AppMember>('list_app_channel_members'),
  })
  const keysQuery = useQuery({
    queryKey: ['admin-client-build-keys'],
    queryFn: () => adminList<ClientBuildKey>('list_client_build_keys'),
  })
  const capabilitiesQuery = useQuery({
    queryKey: ['admin-client-capability-policies'],
    queryFn: () =>
      adminRows<ClientCapabilityPolicy>('list_client_capability_policies'),
  })
  const auditQuery = useQuery({
    queryKey: ['admin-client-trust-audit'],
    queryFn: () =>
      adminList<ClientTrustAudit>('list_client_trust_audit_logs', {
        p_limit: 30,
      }),
  })

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-app-releases'] }),
      queryClient.invalidateQueries({
        queryKey: ['admin-app-update-channels'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['admin-app-update-deployments'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['admin-app-channel-members'],
      }),
      queryClient.invalidateQueries({ queryKey: ['admin-client-build-keys'] }),
      queryClient.invalidateQueries({
        queryKey: ['admin-client-capability-policies'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['admin-client-trust-audit'],
      }),
    ])
  }

  const copyMutation = useMutation({
    mutationFn: ({
      release,
      values,
    }: {
      release: AppRelease
      values: CopyValues
    }) =>
      adminOk('update_app_release_copy', {
        p_id: release.id,
        p_expected_revision: release.revision,
        p_release_notes: values.release_notes,
        p_update_message: values.update_message ?? '',
        p_reason: values.reason || null,
      }),
    onSuccess: async () => {
      message.success('更新文案已保存')
      setCopyTarget(null)
      await invalidate()
    },
    onError: (error: Error) => message.error(formatRpcError(error.message)),
  })

  const deploymentPolicyMutation = useMutation({
    mutationFn: ({
      deployment,
      values,
    }: {
      deployment: AppDeployment
      values: DeploymentPolicyValues
    }) =>
      adminOk('update_app_update_deployment_policy', {
        p_id: deployment.id,
        p_expected_revision: deployment.revision,
        p_staged_rollout_percent: values.staged_rollout_percent,
        p_min_supported_code: values.min_supported_code ?? null,
        p_reason: values.reason,
      }),
    onSuccess: async () => {
      message.success('投放策略已更新')
      setPolicyTarget(null)
      await invalidate()
    },
    onError: (error: Error) => message.error(formatRpcError(error.message)),
  })

  const deploymentStateMutation = useMutation({
    mutationFn: ({
      deployment,
      isActive,
      reason,
    }: {
      deployment: AppDeployment
      isActive: boolean
      reason?: string
    }) =>
      adminOk('set_app_update_deployment_active', {
        p_id: deployment.id,
        p_expected_revision: deployment.revision,
        p_is_active: isActive,
        p_reason: reason || null,
      }),
    onSuccess: async (_, variables) => {
      message.success(variables.isActive ? '部署已启用' : '部署已停用')
      setDeactivateTarget(null)
      setDetail(null)
      await invalidate()
    },
    onError: (error: Error) => message.error(formatRpcError(error.message)),
  })

  const capabilityMutation = useMutation({
    mutationFn: ({
      policy,
      values,
    }: {
      policy: ClientCapabilityPolicy
      values: CapabilityValues
    }) =>
      adminOk('update_client_capability_policy', {
        p_capability: policy.capability,
        p_expected_revision: policy.revision,
        p_mode: values.mode,
        p_soft_min_version_code: values.soft_min_version_code ?? null,
        p_hard_min_version_code: values.hard_min_version_code ?? null,
        p_emergency_grace_seconds: values.emergency_grace_seconds,
        p_grace_deadline: null,
        p_reason: values.reason,
      }),
    onSuccess: async () => {
      message.success('客户端能力策略已更新')
      setCapabilityTarget(null)
      await invalidate()
    },
    onError: (error: Error) => message.error(formatRpcError(error.message)),
  })

  const disableCapabilityMutation = useMutation({
    mutationFn: ({
      policy,
      reason,
    }: {
      policy: ClientCapabilityPolicy
      reason: string
    }) =>
      adminOk('disable_client_capability', {
        p_capability: policy.capability,
        p_reason: reason,
      }),
    onSuccess: async () => {
      message.success('能力策略已紧急关闭')
      setDisableCapabilityTarget(null)
      await invalidate()
    },
    onError: (error: Error) => message.error(formatRpcError(error.message)),
  })

  const channelMutation = useMutation({
    mutationFn: (values: { slug: string; priority: number; reason: string }) =>
      adminOk('upsert_app_update_channel', {
        p_slug: values.slug,
        p_assignment_mode: 'device',
        p_priority: values.priority,
        p_is_active: true,
        p_reason: values.reason,
      }),
    onSuccess: async () => {
      message.success('定向渠道已保存')
      setChannelDrawerOpen(false)
      await invalidate()
    },
    onError: (error: Error) => message.error(formatRpcError(error.message)),
  })

  const memberMutation = useMutation({
    mutationFn: (values: {
      device_id: string
      channel_slug: string
      note?: string
      reason: string
    }) =>
      adminOk('upsert_app_channel_member', {
        p_device_id: values.device_id,
        p_channel_slug: values.channel_slug,
        p_note: values.note || null,
        p_reason: values.reason,
      }),
    onSuccess: async () => {
      message.success('设备定向已保存')
      setMemberDrawerOpen(false)
      await invalidate()
    },
    onError: (error: Error) => message.error(formatRpcError(error.message)),
  })

  const registerBuildKeyMutation = useMutation({
    mutationFn: (values: BuildKeySubmission) =>
      adminOk('register_client_build_key', {
        p_key_id: values.key_id.trim(),
        p_app_id: values.app_id.trim(),
        p_version_code: values.version_code,
        p_version_name: values.version_name.trim(),
        p_channel: values.channel.trim(),
        p_signing_cert_sha256: values.signing_cert_sha256.trim().toLowerCase(),
        p_build_secret: buildSecretRef.current,
        p_not_before: toOptionalIsoString(values.not_before),
        p_expires_at: toOptionalIsoString(values.expires_at),
        p_reason: values.reason.trim(),
        p_request_id: values.request_id?.trim() || createBuildKeyRequestId(),
      }),
    onSuccess: async () => {
      buildSecretRef.current = ''
      message.success('构建 key 已签发')
      setBuildKeyDrawerOpen(false)
      await invalidate()
    },
    onError: (error: Error) => {
      buildSecretRef.current = ''
      message.error(formatRpcError(error.message))
    },
  })

  const revokeBuildKeyMutation = useMutation({
    mutationFn: ({ key, reason }: { key: ClientBuildKey; reason: string }) =>
      adminOk('revoke_client_build_key', {
        p_key_id: key.key_id,
        p_expected_revision: key.revision,
        p_reason: reason,
        p_request_id: createBuildKeyRequestId('revoke'),
      }),
    onSuccess: async () => {
      message.success('构建 key 已撤回')
      setRevokeKeyTarget(null)
      await invalidate()
    },
    onError: (error: Error) => message.error(formatRpcError(error.message)),
  })

  const releases = releasesQuery.data?.rows ?? []
  const deployments = useMemo(
    () =>
      (deploymentsQuery.data?.rows ?? []).map((deployment) => ({
        ...deployment,
        release: {
          ...deployment.release,
          ...releases.find((release) => release.id === deployment.release_id),
        },
      })),
    [deploymentsQuery.data?.rows, releases],
  )
  const channels = channelsQuery.data?.rows ?? []
  const keys = keysQuery.data?.rows ?? []
  const capabilities = capabilitiesQuery.data?.rows ?? []
  const members = membersQuery.data?.rows ?? []
  const audits = auditQuery.data?.rows ?? []
  const loading = [
    releasesQuery,
    channelsQuery,
    deploymentsQuery,
    membersQuery,
    keysQuery,
    capabilitiesQuery,
    auditQuery,
  ].some((query) => query.isFetching)

  const activeByChannel = useMemo(() => {
    const map = new Map<string, AppDeployment>()
    for (const deployment of deployments) {
      if (!deployment.is_active) continue
      const current = map.get(deployment.channel_slug)
      if (
        !current ||
        deployment.release.version_code > current.release.version_code
      ) {
        map.set(deployment.channel_slug, deployment)
      }
    }
    return map
  }, [deployments])

  const latestCiCommit = audits.find(
    (item) =>
      item.actor_kind === 'github_actions' &&
      item.action === 'build_key.commit_release',
  )
  const playbackPolicy = capabilities.find(
    (item) => item.capability === 'android.playback.issue',
  )
  const webPlaybackPolicy = capabilities.find(
    (item) => item.capability === 'web.playback.issue',
  )
  const activeKeys = keys.filter((item) => item.status === 'active')

  const runtimeRows = channels
    .filter((channel) => channel.is_active)
    .map((channel) => ({
      channel,
      deployment: activeByChannel.get(channel.slug),
    }))

  const visibleDeployments = deployments.filter(
    (item) => deploymentFilter === 'all' || item.is_active,
  )
  const visibleKeys = keys.filter(
    (item) =>
      (keyPlatformFilter === 'all' ||
        buildKeyPlatform(item.key_id) === keyPlatformFilter) &&
      (keyFilter === 'all' ||
        (keyFilter === 'active' && item.status === 'active') ||
        (keyFilter === 'revoked' && item.status !== 'active')),
  )

  const submitBuildKey = (values: BuildKeyValues) => {
    buildSecretRef.current = values.build_secret.trim()
    registerBuildKeyMutation.mutate(withoutBuildSecret(values))
  }

  const runtimeColumns: ColumnsType<(typeof runtimeRows)[number]> = [
    {
      title: '更新渠道',
      key: 'channel',
      width: 180,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          {channelTag(row.channel.slug, row.channel.assignment_mode)}
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.channel.assignment_mode === 'device'
              ? '设备名单命中'
              : '由客户端构建渠道选择'}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: '当前候选版本',
      key: 'release',
      render: (_, row) =>
        row.deployment ? (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>
              {row.deployment.release.version_name}
            </Typography.Text>
            <Typography.Text type="secondary" className="app-update-mono">
              code {row.deployment.release.version_code} · deployment #
              {row.deployment.id}
            </Typography.Text>
          </Space>
        ) : (
          <Tag color="warning">无生效部署</Tag>
        ),
    },
    {
      title: '投放范围',
      key: 'rollout',
      width: 190,
      render: (_, row) =>
        row.deployment ? (
          <Progress
            percent={row.deployment.staged_rollout_percent}
            size="small"
            status="normal"
            strokeColor={
              row.deployment.staged_rollout_percent < 100
                ? '#d48806'
                : undefined
            }
          />
        ) : (
          '—'
        ),
    },
    {
      title: '最低可用版本',
      key: 'floor',
      width: 150,
      render: (_, row) =>
        row.deployment?.min_supported_code ? (
          <Tag color="red">code {row.deployment.min_supported_code}</Tag>
        ) : (
          <Typography.Text type="secondary">不强制更新</Typography.Text>
        ),
    },
    {
      title: '发布于',
      key: 'published',
      width: 172,
      render: (_, row) => formatCST(row.deployment?.published_at),
    },
    {
      title: '操作',
      key: 'actions',
      width: 128,
      render: (_, row) =>
        row.deployment ? (
          <Space size={2}>
            <Button
              type="link"
              size="small"
              onClick={() => setPolicyTarget(row.deployment!)}
            >
              调整策略
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => setDetail(row.deployment!)}
            >
              详情
            </Button>
          </Space>
        ) : null,
    },
  ]

  const deploymentColumns: ColumnsType<AppDeployment> = [
    {
      title: '版本',
      key: 'release',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{row.release.version_name}</Typography.Text>
          <Typography.Text type="secondary" className="app-update-mono">
            code {row.release.version_code}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: '渠道',
      dataIndex: 'channel_slug',
      width: 130,
      render: (value: string, row) =>
        channelTag(value, row.channel.assignment_mode),
    },
    {
      title: '灰度',
      dataIndex: 'staged_rollout_percent',
      width: 90,
      render: (value: number) => (
        <span className="app-update-mono">{value}%</span>
      ),
    },
    {
      title: '强更下限',
      dataIndex: 'min_supported_code',
      width: 140,
      render: (value: number | null) =>
        value ? (
          <Tag color="red">code {value}</Tag>
        ) : (
          <Typography.Text type="secondary">无</Typography.Text>
        ),
    },
    {
      title: '状态',
      key: 'state',
      width: 100,
      render: (_, row) => deploymentState(row),
    },
    {
      title: '发布时间',
      dataIndex: 'published_at',
      width: 172,
      render: (value) => formatCST(value),
    },
    {
      title: '操作',
      key: 'action',
      width: 170,
      render: (_, row) => (
        <Space size={2}>
          <Button type="link" size="small" onClick={() => setDetail(row)}>
            详情
          </Button>
          {row.is_active ? (
            <Button
              type="link"
              size="small"
              onClick={() => setPolicyTarget(row)}
            >
              策略
            </Button>
          ) : (
            <Button
              type="link"
              size="small"
              onClick={() =>
                deploymentStateMutation.mutate({
                  deployment: row,
                  isActive: true,
                })
              }
            >
              启用
            </Button>
          )}
        </Space>
      ),
    },
  ]

  const releaseColumns: ColumnsType<AppRelease> = [
    {
      title: '版本',
      key: 'version',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            <Typography.Text strong>{row.version_name}</Typography.Text>
            {row.first_published_at ? (
              <Tag color="success">CI 已发布</Tag>
            ) : (
              <Tag color="warning">草稿</Tag>
            )}
          </Space>
          <Typography.Text type="secondary" className="app-update-mono">
            code {row.version_code} · release #{row.id}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'minSdk',
      dataIndex: 'min_sdk',
      width: 90,
      render: (value) => value ?? '—',
    },
    {
      title: 'APK',
      dataIndex: 'apk_size_bytes',
      width: 110,
      render: formatBytes,
    },
    {
      title: 'Client Trust',
      key: 'trust',
      width: 150,
      render: (_, row) => {
        const key = keys.find(
          (item) =>
            item.version_code === row.version_code && item.status === 'active',
        )
        return key ? (
          <Tag color="processing" icon={<KeyOutlined />}>
            密钥有效
          </Tag>
        ) : (
          <Typography.Text type="secondary">无活动密钥</Typography.Text>
        )
      },
    },
    {
      title: '首次发布',
      dataIndex: 'first_published_at',
      width: 172,
      render: (value) => formatCST(value),
    },
    {
      title: 'revision',
      dataIndex: 'revision',
      width: 82,
      render: (value) => <span className="app-update-mono">r{value}</span>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 174,
      render: (_, row) => (
        <Space size={2}>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => setCopyTarget(row)}
          >
            更新文案
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setDetail(row)}
          >
            详情
          </Button>
        </Space>
      ),
    },
  ]

  const capabilityColumns: ColumnsType<ClientCapabilityPolicy> = [
    {
      title: '客户端能力',
      dataIndex: 'capability',
      render: (value: string) => (
        <Typography.Text className="app-update-mono">{value}</Typography.Text>
      ),
    },
    { title: '模式', dataIndex: 'mode', width: 116, render: capabilityModeTag },
    {
      title: '版本门槛',
      key: 'floors',
      render: (_, row) => (
        <Space size={8}>
          <span>
            soft{' '}
            <Typography.Text className="app-update-mono">
              {row.soft_min_version_code ?? '—'}
            </Typography.Text>
          </span>
          <Divider type="vertical" />
          <span>
            hard{' '}
            <Typography.Text className="app-update-mono">
              {row.hard_min_version_code ?? '—'}
            </Typography.Text>
          </span>
        </Space>
      ),
    },
    {
      title: '紧急宽限',
      dataIndex: 'emergency_grace_seconds',
      width: 118,
      render: (value: number) => (value ? `${value}s` : '无'),
    },
    {
      title: '更新于',
      dataIndex: 'updated_at',
      width: 172,
      render: (value) => formatCST(value),
    },
    {
      title: '操作',
      key: 'actions',
      width: 268,
      render: (_, row) => (
        <Space size={2}>
          <Button
            type="link"
            size="small"
            onClick={() => setCapabilityTarget(row)}
          >
            配置
          </Button>
          {row.capability === 'android.playback.issue' &&
          (row.mode === 'observe' || row.mode === 'soft') ? (
            <Button
              danger
              type="link"
              size="small"
              icon={<SafetyCertificateOutlined />}
              onClick={() => setEnforceTarget(row)}
            >
              进入 enforce
            </Button>
          ) : null}
          {row.mode !== 'disabled' ? (
            <Button
              danger
              type="link"
              size="small"
              onClick={() => setDisableCapabilityTarget(row)}
            >
              紧急关闭
            </Button>
          ) : null}
        </Space>
      ),
    },
  ]

  const keyColumns: ColumnsType<ClientBuildKey> = [
    {
      title: '构建身份',
      key: 'identity',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Space size={6}>
            {buildKeyPlatformTag(row)}
            <Typography.Text strong>{row.version_name}</Typography.Text>
            {buildKeyStatusTag(row)}
          </Space>
          <Tooltip title={row.key_id}>
            <Typography.Text
              type="secondary"
              ellipsis
              className="app-update-mono"
              style={{ maxWidth: 330 }}
            >
              {row.key_id}
            </Typography.Text>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'channel',
      dataIndex: 'channel',
      width: 104,
      render: (value) => <Tag>{value}</Tag>,
    },
    {
      title: 'versionCode',
      dataIndex: 'version_code',
      width: 126,
      render: (value) => <span className="app-update-mono">{value}</span>,
    },
    {
      title: '有效窗口',
      key: 'window',
      width: 206,
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Typography.Text style={{ fontSize: 12 }}>
            自 {formatCST(row.not_before)}
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.expires_at ? `至 ${formatCST(row.expires_at)}` : '长期有效'}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: '最近匹配',
      dataIndex: 'last_matched_at',
      width: 172,
      render: (value) => formatCST(value),
    },
    {
      title: 'revision',
      dataIndex: 'revision',
      width: 82,
      render: (value) => <span className="app-update-mono">r{value}</span>,
    },
    {
      title: '操作',
      key: 'actions',
      width: 84,
      fixed: 'right',
      render: (_, row) => (
        <Button
          danger
          disabled={row.status !== 'active'}
          size="small"
          type="link"
          onClick={() => setRevokeKeyTarget(row)}
        >
          撤回
        </Button>
      ),
    },
  ]

  const channelColumns: ColumnsType<AppChannel> = [
    {
      title: '渠道',
      dataIndex: 'slug',
      render: (value, row) => channelTag(value, row.assignment_mode),
    },
    {
      title: '分配方式',
      dataIndex: 'assignment_mode',
      render: (value) => (value === 'device' ? '设备名单' : '客户端构建'),
    },
    {
      title: '优先级',
      dataIndex: 'priority',
      width: 100,
      render: (value) => <span className="app-update-mono">{value}</span>,
    },
    {
      title: '生效部署',
      dataIndex: 'active_deployment_count',
      width: 110,
      render: (value) => value ?? 0,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 100,
      render: (value) =>
        value ? <Tag color="success">启用</Tag> : <Tag>停用</Tag>,
    },
  ]

  const memberColumns: ColumnsType<AppMember> = [
    {
      title: '设备 hint',
      dataIndex: 'device_hint',
      render: (value) => <span className="app-update-mono">••••{value}</span>,
    },
    {
      title: '渠道',
      dataIndex: 'channel_slug',
      width: 150,
      render: (value) => channelTag(value, 'device'),
    },
    { title: '备注', dataIndex: 'note', render: (value) => value || '—' },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 100,
      render: (value) =>
        value ? <Tag color="success">启用</Tag> : <Tag>停用</Tag>,
    },
    {
      title: '更新于',
      dataIndex: 'updated_at',
      width: 172,
      render: (value) => formatCST(value),
    },
  ]

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card className="app-update-page" styles={{ body: { paddingTop: 16 } }}>
        <div className="app-update-heading">
          <Space size={8} wrap>
            <AndroidOutlined />
            <Typography.Title level={4} style={{ margin: 0 }}>
              App 发布控制台
            </Typography.Title>
            <Tag color="green">Android</Tag>
            <Tag>CI 托管发布</Tag>
          </Space>
          {isMobile ? (
            <Tooltip title="刷新">
              <Button
                icon={<ReloadOutlined />}
                loading={loading}
                aria-label="刷新"
                onClick={() => void invalidate()}
              />
            </Tooltip>
          ) : (
            <Button
              icon={<ReloadOutlined />}
              loading={loading}
              onClick={() => void invalidate()}
            >
              刷新
            </Button>
          )}
        </div>

        <Alert
          type="info"
          showIcon
          message="版本由 GitHub Actions 原子发布"
          description={
            <>
              构建流水线负责 APK、release、deployment 与 Client Trust build key
              的一致性提交。管理中心只调整线上策略与文案，不再手工上传 APK
              或新建版本。
              {/* 手机上 action 列会把正文挤成一字一行，改为附在正文下方 */}
              {isMobile && latestCiCommit ? (
                <Typography.Text
                  type="secondary"
                  style={{ display: 'block', marginTop: 4, fontSize: 12 }}
                >
                  最近提交 {formatCST(latestCiCommit.occurred_at)}
                </Typography.Text>
              ) : null}
            </>
          }
          action={
            !isMobile && latestCiCommit ? (
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, whiteSpace: 'nowrap' }}
              >
                最近提交 {formatCST(latestCiCommit.occurred_at)}
              </Typography.Text>
            ) : null
          }
          style={{ marginBottom: 16 }}
        />

        <div className="app-update-status-strip" aria-label="发布运行状态">
          <div className="app-update-status-item">
            <Typography.Text type="secondary">stable</Typography.Text>
            <strong>
              {activeByChannel.get('stable')?.release.version_name ??
                '无生效版本'}
            </strong>
            <Typography.Text type="secondary" className="app-update-mono">
              {activeByChannel.get('stable')
                ? `code ${activeByChannel.get('stable')!.release.version_code}`
                : '—'}
            </Typography.Text>
          </div>
          <div className="app-update-status-item">
            <Typography.Text type="secondary">beta</Typography.Text>
            <strong>
              {activeByChannel.get('beta')?.release.version_name ??
                '无生效版本'}
            </strong>
            <Typography.Text type="secondary">
              {activeByChannel.get('beta')
                ? `${activeByChannel.get('beta')!.staged_rollout_percent}% 投放`
                : '—'}
            </Typography.Text>
          </div>
          <div className="app-update-status-item">
            <Typography.Text type="secondary">客户端准入</Typography.Text>
            <strong>
              {playbackPolicy ? modeLabel(playbackPolicy.mode) : '未配置'}
            </strong>
            <Typography.Text type="secondary">
              {playbackPolicy?.hard_min_version_code
                ? `hard ≥ ${playbackPolicy.hard_min_version_code}`
                : '无硬门槛'}
            </Typography.Text>
          </div>
          <div className="app-update-status-item">
            <Typography.Text type="secondary">Client Trust</Typography.Text>
            <strong>{activeKeys.length} 个活动密钥</strong>
            <Typography.Text type="secondary">
              {BUILD_KEY_PLATFORMS.map(
                (platform) =>
                  `${BUILD_KEY_PLATFORM_COPY[platform].label} ${
                    activeKeys.filter(
                      (item) => buildKeyPlatform(item.key_id) === platform,
                    ).length
                  }`,
              ).join(' · ')}
              {` / 共 ${keys.length} 个构建身份`}
            </Typography.Text>
          </div>
        </div>

        <div className="admin-scroll-rail app-update-tab-rail">
          <Segmented
            block={!isMobile}
            value={tab}
            onChange={(value) => setTab(value as TabKey)}
            options={[
              {
                label: '运行态',
                value: 'runtime',
                icon: <CloudServerOutlined />,
              },
              {
                label: isMobile
                  ? `版本 ${releases.length}`
                  : `版本档案 ${releases.length}`,
                value: 'releases',
                icon: <AndroidOutlined />,
              },
              {
                label: isMobile ? '准入' : '客户端准入',
                value: 'trust',
                icon: <SafetyCertificateOutlined />,
              },
              {
                label: isMobile ? '渠道' : '渠道与定向',
                value: 'channels',
                icon: <SettingOutlined />,
              },
            ]}
          />
        </div>

        {tab === 'runtime' ? (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <section aria-labelledby="runtime-channels-title">
              <div className="app-update-toolbar">
                <Space direction="vertical" size={0}>
                  <Typography.Text strong id="runtime-channels-title">
                    客户端当前会拿到什么
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    按渠道展示最高 versionCode 的活动部署；灰度命中仍由
                    rollout_seed 稳定分桶。
                  </Typography.Text>
                </Space>
              </div>
              <div className="admin-table-wrap">
                <Table
                  rowKey={(row) => row.channel.slug}
                  size="middle"
                  loading={loading}
                  columns={runtimeColumns}
                  dataSource={runtimeRows}
                  pagination={false}
                  scroll={{ x: 1050 }}
                />
              </div>
              <AdminMobileList
                rows={runtimeRows}
                rowKey={(row) => row.channel.slug}
                loading={loading}
                renderItem={(row) => {
                  const deployment = row.deployment
                  return (
                    <MobileRecord
                      title={channelTag(
                        row.channel.slug,
                        row.channel.assignment_mode,
                      )}
                      status={
                        deployment ? (
                          <Typography.Text className="app-update-mono">
                            {deployment.staged_rollout_percent}%
                          </Typography.Text>
                        ) : (
                          <Tag color="warning">无生效部署</Tag>
                        )
                      }
                      onOpen={
                        deployment ? () => setDetail(deployment) : undefined
                      }
                      actions={
                        deployment
                          ? [
                              {
                                key: 'policy',
                                label: '调整策略',
                                icon: <SettingOutlined />,
                                onClick: () => setPolicyTarget(deployment),
                              },
                            ]
                          : undefined
                      }
                    >
                      {deployment ? (
                        <>
                          <div className="admin-mobile-record__body">
                            <Typography.Text strong>
                              {deployment.release.version_name}
                            </Typography.Text>{' '}
                            <Typography.Text
                              type="secondary"
                              className="app-update-mono"
                            >
                              code {deployment.release.version_code}
                            </Typography.Text>
                          </div>
                          <Progress
                            percent={deployment.staged_rollout_percent}
                            size="small"
                            showInfo={false}
                            strokeColor={
                              deployment.staged_rollout_percent < 100
                                ? '#d48806'
                                : undefined
                            }
                          />
                          <div className="admin-mobile-record__meta">
                            <span>
                              {deployment.min_supported_code
                                ? `强更下限 code ${deployment.min_supported_code}`
                                : '不强制更新'}
                            </span>
                            <span>
                              发布{' '}
                              {formatCST(
                                deployment.published_at,
                                'MM-DD HH:mm',
                              )}
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="admin-mobile-record__meta">
                          <span>
                            {row.channel.assignment_mode === 'device'
                              ? '设备名单命中'
                              : '由客户端构建渠道选择'}
                          </span>
                        </div>
                      )}
                    </MobileRecord>
                  )
                }}
              />
            </section>
            <Divider style={{ margin: 0 }} />
            <section aria-labelledby="deployment-history-title">
              <div className="app-update-toolbar">
                <Typography.Text strong id="deployment-history-title">
                  部署记录
                </Typography.Text>
                <Segmented
                  size="small"
                  value={deploymentFilter}
                  onChange={(value) => {
                    setDeploymentFilter(value as DeploymentFilter)
                    setDeploymentPage(1)
                  }}
                  options={[
                    { label: '仅生效', value: 'active' },
                    { label: '全部', value: 'all' },
                  ]}
                />
              </div>
              <div className="admin-table-wrap">
                <Table
                  rowKey="id"
                  size="middle"
                  columns={deploymentColumns}
                  dataSource={visibleDeployments}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  scroll={{ x: 980 }}
                />
              </div>
              <AdminMobileList
                rows={pageSlice(visibleDeployments, deploymentPage)}
                rowKey={(row) => row.id}
                loading={deploymentsQuery.isLoading}
                page={deploymentPage}
                pageSize={MOBILE_PAGE_SIZE}
                total={visibleDeployments.length}
                onPageChange={setDeploymentPage}
                renderItem={(row) => (
                  <MobileRecord
                    title={row.release.version_name}
                    status={deploymentState(row)}
                    onOpen={() => setDetail(row)}
                    actions={[
                      row.is_active
                        ? {
                            key: 'policy',
                            label: '策略',
                            icon: <SettingOutlined />,
                            onClick: () => setPolicyTarget(row),
                          }
                        : {
                            key: 'enable',
                            label: '启用',
                            icon: <CheckCircleOutlined />,
                            loading: deploymentStateMutation.isPending,
                            onClick: () =>
                              deploymentStateMutation.mutate({
                                deployment: row,
                                isActive: true,
                              }),
                          },
                    ]}
                  >
                    <div className="admin-mobile-record__badges">
                      {channelTag(
                        row.channel_slug,
                        row.channel.assignment_mode,
                      )}
                      <Tag>灰度 {row.staged_rollout_percent}%</Tag>
                      {row.min_supported_code ? (
                        <Tag color="red">
                          强更 code {row.min_supported_code}
                        </Tag>
                      ) : null}
                    </div>
                    <div className="admin-mobile-record__meta">
                      <span className="app-update-mono">
                        code {row.release.version_code}
                      </span>
                      <span>
                        发布 {formatCST(row.published_at, 'MM-DD HH:mm')}
                      </span>
                    </div>
                  </MobileRecord>
                )}
              />
            </section>
          </Space>
        ) : null}

        {tab === 'releases' ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Alert
              type="warning"
              showIcon
              message="制品字段只读"
              description="已发布版本的 APK URL、校验值、versionCode 与 minSdk 由 CI 固化。这里仅允许修订客户端展示的 release_notes 与 update_message。"
            />
            <div className="admin-table-wrap">
              <Table
                rowKey="id"
                size="middle"
                loading={releasesQuery.isLoading}
                columns={releaseColumns}
                dataSource={releases}
                pagination={{ pageSize: 20, showSizeChanger: false }}
                scroll={{ x: 1040 }}
              />
            </div>
            <AdminMobileList
              rows={pageSlice(releases, releasePage)}
              rowKey={(row) => row.id}
              loading={releasesQuery.isLoading}
              page={releasePage}
              pageSize={MOBILE_PAGE_SIZE}
              total={releases.length}
              onPageChange={setReleasePage}
              renderItem={(row) => {
                const hasKey = keys.some(
                  (item) =>
                    item.version_code === row.version_code &&
                    item.status === 'active',
                )
                return (
                  <MobileRecord
                    title={row.version_name}
                    status={
                      row.first_published_at ? (
                        <Tag color="success">CI 已发布</Tag>
                      ) : (
                        <Tag color="warning">草稿</Tag>
                      )
                    }
                    onOpen={() => setDetail(row)}
                    actions={[
                      {
                        key: 'copy',
                        label: '更新文案',
                        icon: <EditOutlined />,
                        onClick: () => setCopyTarget(row),
                      },
                    ]}
                  >
                    <div className="admin-mobile-record__meta">
                      <span className="app-update-mono">
                        code {row.version_code}
                      </span>
                      <span>minSdk {row.min_sdk ?? '—'}</span>
                      <span>{formatBytes(row.apk_size_bytes)}</span>
                      <span className="app-update-mono">r{row.revision}</span>
                    </div>
                    <div className="admin-mobile-record__meta">
                      {hasKey ? (
                        <Tag color="processing" icon={<KeyOutlined />}>
                          密钥有效
                        </Tag>
                      ) : (
                        <span>无活动密钥</span>
                      )}
                      <span>
                        首次发布{' '}
                        {formatCST(row.first_published_at, 'MM-DD HH:mm')}
                      </span>
                    </div>
                  </MobileRecord>
                )
              }}
            />
          </Space>
        ) : null}

        {tab === 'trust' ? (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <section aria-labelledby="capability-title">
              <div className="app-update-toolbar">
                <Space direction="vertical" size={0}>
                  <Typography.Text strong id="capability-title">
                    能力门控策略
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    更新提示负责引导升级；能力门控决定旧客户端是否还能获取受保护能力。
                  </Typography.Text>
                </Space>
              </div>
              {playbackPolicy?.mode === 'observe' ? (
                <Alert
                  type="info"
                  showIcon
                  message="android.playback.issue 当前仅观察，不会拒绝旧客户端"
                  style={{ marginBottom: 12 }}
                />
              ) : null}
              <div className="admin-table-wrap">
                <Table
                  rowKey="capability"
                  size="middle"
                  loading={capabilitiesQuery.isLoading}
                  columns={capabilityColumns}
                  dataSource={capabilities}
                  pagination={false}
                  scroll={{ x: 1080 }}
                />
              </div>
              <AdminMobileList
                rows={capabilities}
                rowKey={(row) => row.capability}
                loading={capabilitiesQuery.isLoading}
                renderItem={(row) => (
                  <MobileRecord
                    title={
                      <span className="app-update-mono app-update-break">
                        {row.capability}
                      </span>
                    }
                    status={capabilityModeTag(row.mode)}
                    onOpen={() => setCapabilityTarget(row)}
                    actions={[
                      {
                        key: 'config',
                        label: '配置',
                        icon: <SettingOutlined />,
                        primary: true,
                        onClick: () => setCapabilityTarget(row),
                      },
                      {
                        key: 'enforce',
                        label: '进入 enforce',
                        icon: <SafetyCertificateOutlined />,
                        danger: true,
                        visible:
                          row.capability === 'android.playback.issue' &&
                          (row.mode === 'observe' || row.mode === 'soft'),
                        onClick: () => setEnforceTarget(row),
                      },
                      {
                        key: 'disable',
                        label: '紧急关闭',
                        icon: <StopOutlined />,
                        danger: true,
                        visible: row.mode !== 'disabled',
                        onClick: () => setDisableCapabilityTarget(row),
                      },
                    ]}
                  >
                    <div className="admin-mobile-record__meta">
                      <span>
                        soft{' '}
                        <span className="app-update-mono">
                          {row.soft_min_version_code ?? '—'}
                        </span>
                      </span>
                      <span>
                        hard{' '}
                        <span className="app-update-mono">
                          {row.hard_min_version_code ?? '—'}
                        </span>
                      </span>
                      <span>
                        宽限{' '}
                        {row.emergency_grace_seconds
                          ? `${row.emergency_grace_seconds}s`
                          : '无'}
                      </span>
                      <span>
                        更新 {formatCST(row.updated_at, 'MM-DD HH:mm')}
                      </span>
                    </div>
                  </MobileRecord>
                )}
              />
            </section>
            <Divider style={{ margin: 0 }} />
            <section aria-labelledby="build-keys-title">
              <div className="app-update-toolbar">
                <Space direction="vertical" size={0}>
                  <Typography.Text strong id="build-keys-title">
                    构建身份与密钥
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    只展示 Vault 外的安全元数据；签发时的 secret 仅写入
                    Vault，不会回显或进入列表。
                  </Typography.Text>
                </Space>
                <Space size={8} wrap>
                  <Segmented
                    size="small"
                    value={keyPlatformFilter}
                    onChange={(value) => {
                      setKeyPlatformFilter(value as KeyPlatformFilter)
                      setKeyPage(1)
                    }}
                    options={[
                      { label: '全部平台', value: 'all' },
                      ...BUILD_KEY_PLATFORMS.map((platform) => ({
                        label: BUILD_KEY_PLATFORM_COPY[platform].label,
                        value: platform,
                      })),
                    ]}
                  />
                  <Segmented
                    size="small"
                    value={keyFilter}
                    onChange={(value) => {
                      setKeyFilter(value as KeyFilter)
                      setKeyPage(1)
                    }}
                    options={[
                      { label: '活动', value: 'active' },
                      { label: '已撤销', value: 'revoked' },
                      { label: '全部', value: 'all' },
                    ]}
                  />
                  <Button
                    type="primary"
                    size="small"
                    icon={<KeyOutlined />}
                    onClick={() => setBuildKeyDrawerOpen(true)}
                  >
                    签发 key
                  </Button>
                </Space>
              </div>
              <div className="admin-table-wrap">
                <Table
                  rowKey="key_id"
                  size="middle"
                  loading={keysQuery.isLoading}
                  columns={keyColumns}
                  dataSource={visibleKeys}
                  pagination={{ pageSize: 20, showSizeChanger: false }}
                  scroll={{ x: 1140 }}
                />
              </div>
              <AdminMobileList
                rows={pageSlice(visibleKeys, keyPage)}
                rowKey={(row) => row.key_id}
                loading={keysQuery.isLoading}
                page={keyPage}
                pageSize={MOBILE_PAGE_SIZE}
                total={visibleKeys.length}
                onPageChange={setKeyPage}
                renderItem={(row) => (
                  <MobileRecord
                    title={
                      <Space size={6}>
                        {buildKeyPlatformTag(row)}
                        {row.version_name}
                      </Space>
                    }
                    status={buildKeyStatusTag(row)}
                    actions={[
                      {
                        key: 'revoke',
                        label: '撤回',
                        danger: true,
                        disabled: row.status !== 'active',
                        visible: row.status === 'active',
                        onClick: () => setRevokeKeyTarget(row),
                      },
                    ]}
                  >
                    <div className="admin-mobile-record__body">
                      <Typography.Text
                        type="secondary"
                        copyable={{ text: row.key_id }}
                        className="app-update-mono app-update-break"
                        style={{ fontSize: 12 }}
                      >
                        {row.key_id}
                      </Typography.Text>
                    </div>
                    <div className="admin-mobile-record__meta">
                      <Tag>{row.channel}</Tag>
                      <span className="app-update-mono">
                        code {row.version_code}
                      </span>
                      <span className="app-update-mono">r{row.revision}</span>
                    </div>
                    <div className="admin-mobile-record__meta">
                      <span>
                        {formatCST(row.not_before, 'MM-DD HH:mm')} →{' '}
                        {row.expires_at
                          ? formatCST(row.expires_at, 'MM-DD HH:mm')
                          : '长期有效'}
                      </span>
                      <span>
                        最近匹配{' '}
                        {row.last_matched_at
                          ? formatCST(row.last_matched_at, 'MM-DD HH:mm')
                          : '—'}
                      </span>
                    </div>
                  </MobileRecord>
                )}
              />
            </section>
            <Divider style={{ margin: 0 }} />
            <section aria-labelledby="ci-audit-title">
              <Typography.Text strong id="ci-audit-title">
                最近 CI 发布链路
              </Typography.Text>
              <div className="app-update-ci-list">
                {audits
                  .filter((item) => item.actor_kind === 'github_actions')
                  .slice(0, 8)
                  .map((item) => (
                    <div className="app-update-ci-row" key={item.id}>
                      <CheckCircleOutlined className="app-update-ci-icon" />
                      <div>
                        <Typography.Text>
                          {auditActionLabel(item.action)}
                        </Typography.Text>
                        <Typography.Text
                          type="secondary"
                          className="app-update-mono"
                          ellipsis
                          style={{ display: 'block', maxWidth: '100%' }}
                        >
                          {item.target_id}
                        </Typography.Text>
                      </div>
                      <Typography.Text
                        type="secondary"
                        className="app-update-ci-time"
                      >
                        {formatCST(item.occurred_at)}
                      </Typography.Text>
                    </div>
                  ))}
                {!audits.some(
                  (item) => item.actor_kind === 'github_actions',
                ) ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="暂无 CI 审计记录"
                  />
                ) : null}
              </div>
            </section>
          </Space>
        ) : null}

        {tab === 'channels' ? (
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <section aria-labelledby="channels-title">
              <div className="app-update-toolbar">
                <Space direction="vertical" size={0}>
                  <Typography.Text strong id="channels-title">
                    更新渠道
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    stable / beta 由构建声明；设备渠道用于少量定向验证。
                  </Typography.Text>
                </Space>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setChannelDrawerOpen(true)}
                >
                  新建设备渠道
                </Button>
              </div>
              <div className="admin-table-wrap">
                <Table
                  rowKey="slug"
                  size="middle"
                  loading={channelsQuery.isLoading}
                  columns={channelColumns}
                  dataSource={channels}
                  pagination={false}
                />
              </div>
              <AdminMobileList
                rows={channels}
                rowKey={(row) => row.slug}
                loading={channelsQuery.isLoading}
                renderItem={(row) => (
                  <MobileRecord
                    title={channelTag(row.slug, row.assignment_mode)}
                    status={
                      row.is_active ? (
                        <Tag color="success">启用</Tag>
                      ) : (
                        <Tag>停用</Tag>
                      )
                    }
                  >
                    <div className="admin-mobile-record__meta">
                      <span>
                        {row.assignment_mode === 'device'
                          ? '设备名单'
                          : '客户端构建'}
                      </span>
                      <span>
                        优先级{' '}
                        <span className="app-update-mono">{row.priority}</span>
                      </span>
                      <span>生效部署 {row.active_deployment_count ?? 0}</span>
                    </div>
                  </MobileRecord>
                )}
              />
            </section>
            <Divider style={{ margin: 0 }} />
            <section aria-labelledby="members-title">
              <div className="app-update-toolbar">
                <Space direction="vertical" size={0}>
                  <Typography.Text strong id="members-title">
                    设备定向成员
                  </Typography.Text>
                  <Typography.Text type="secondary">
                    数据库只返回设备哈希的末尾 hint，无法反查明文设备 ID。
                  </Typography.Text>
                </Space>
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setMemberDrawerOpen(true)}
                >
                  添加设备
                </Button>
              </div>
              <div className="admin-table-wrap">
                <Table
                  rowKey="id"
                  size="middle"
                  loading={membersQuery.isLoading}
                  columns={memberColumns}
                  dataSource={members}
                  pagination={false}
                />
              </div>
              <AdminMobileList
                rows={members}
                rowKey={(row) => row.id}
                loading={membersQuery.isLoading}
                renderItem={(row) => (
                  <MobileRecord
                    title={
                      <span className="app-update-mono">
                        ••••{row.device_hint}
                      </span>
                    }
                    status={
                      row.is_active ? (
                        <Tag color="success">启用</Tag>
                      ) : (
                        <Tag>停用</Tag>
                      )
                    }
                  >
                    {row.note ? (
                      <div className="admin-mobile-record__body">
                        {row.note}
                      </div>
                    ) : null}
                    <div className="admin-mobile-record__meta">
                      {channelTag(row.channel_slug, 'device')}
                      <span>
                        更新 {formatCST(row.updated_at, 'MM-DD HH:mm')}
                      </span>
                    </div>
                  </MobileRecord>
                )}
              />
            </section>
          </Space>
        ) : null}
      </Card>

      <EditCopyDrawer
        release={copyTarget}
        submitting={copyMutation.isPending}
        onClose={() => setCopyTarget(null)}
        onSubmit={(values) =>
          copyTarget && copyMutation.mutate({ release: copyTarget, values })
        }
      />
      <DeploymentPolicyDrawer
        deployment={policyTarget}
        submitting={deploymentPolicyMutation.isPending}
        onClose={() => setPolicyTarget(null)}
        onSubmit={(values) =>
          policyTarget &&
          deploymentPolicyMutation.mutate({ deployment: policyTarget, values })
        }
      />
      <CapabilityDrawer
        policy={capabilityTarget}
        submitting={capabilityMutation.isPending}
        onClose={() => setCapabilityTarget(null)}
        onSubmit={(values) =>
          capabilityTarget &&
          capabilityMutation.mutate({ policy: capabilityTarget, values })
        }
      />
      <BuildKeyDrawer
        open={buildKeyDrawerOpen}
        submitting={registerBuildKeyMutation.isPending}
        onClose={() => {
          buildSecretRef.current = ''
          setBuildKeyDrawerOpen(false)
        }}
        onSubmit={submitBuildKey}
      />
      <AndroidEnforceDrawer
        open={!!enforceTarget}
        policy={enforceTarget}
        webPolicy={webPlaybackPolicy ?? null}
        stableDeployment={activeByChannel.get('stable') ?? null}
        buildKeys={keys}
        onClose={() => setEnforceTarget(null)}
        onPreflight={async (args: AndroidEnforcePreflightArgs) => {
          const result = expectOk(
            await adminRpc('preflight_android_capability_enforcement', {
              ...args,
            }),
          )
          return result as AndroidEnforcePreflightResult
        }}
        onEnable={async (args: ClientCapabilityUpdateArgs) => {
          await adminOk('update_client_capability_policy', { ...args })
          await invalidate()
        }}
        formatError={formatRpcError}
      />
      <DetailDrawer
        value={detail}
        pending={deploymentStateMutation.isPending}
        onClose={() => setDetail(null)}
        onDeactivate={(deployment) => setDeactivateTarget(deployment)}
      />
      <ReasonModal
        open={!!deactivateTarget}
        title="停用部署"
        danger
        confirmText="停用部署"
        description={
          deactivateTarget
            ? `停用 ${deactivateTarget.release.version_name} 在 ${deactivateTarget.channel_slug} 的部署。客户端将立即停止选择它，操作不可自动撤销。`
            : ''
        }
        pending={deploymentStateMutation.isPending}
        onClose={() => setDeactivateTarget(null)}
        onSubmit={(reason) =>
          deactivateTarget &&
          deploymentStateMutation.mutate({
            deployment: deactivateTarget,
            isActive: false,
            reason,
          })
        }
      />
      <ReasonModal
        open={!!disableCapabilityTarget}
        title="紧急关闭客户端能力"
        danger
        confirmText="立即关闭"
        description={
          disableCapabilityTarget
            ? `将 ${disableCapabilityTarget.capability} 切换为 disabled，并清除宽限设置。此操作会写入独立审计记录。`
            : ''
        }
        pending={disableCapabilityMutation.isPending}
        onClose={() => setDisableCapabilityTarget(null)}
        onSubmit={(reason) =>
          disableCapabilityTarget &&
          disableCapabilityMutation.mutate({
            policy: disableCapabilityTarget,
            reason,
          })
        }
      />
      <ReasonModal
        open={!!revokeKeyTarget}
        title="撤回构建 key"
        danger
        confirmText="撤回 key"
        description={
          revokeKeyTarget
            ? `撤回 ${revokeKeyTarget.key_id}（revision r${revokeKeyTarget.revision}）。撤回后该构建身份不能再通过 Client Trust 验证，操作不可自动恢复。`
            : ''
        }
        pending={revokeBuildKeyMutation.isPending}
        onClose={() => setRevokeKeyTarget(null)}
        onSubmit={(reason) =>
          revokeKeyTarget &&
          revokeBuildKeyMutation.mutate({ key: revokeKeyTarget, reason })
        }
      />
      <ChannelDrawer
        open={channelDrawerOpen}
        pending={channelMutation.isPending}
        onClose={() => setChannelDrawerOpen(false)}
        onSubmit={(values) => channelMutation.mutate(values)}
      />
      <MemberDrawer
        open={memberDrawerOpen}
        pending={memberMutation.isPending}
        channels={channels.filter(
          (channel) => channel.assignment_mode === 'device',
        )}
        onClose={() => setMemberDrawerOpen(false)}
        onSubmit={(values) => memberMutation.mutate(values)}
      />
    </Space>
  )
}

function EditCopyDrawer({
  release,
  submitting,
  onClose,
  onSubmit,
}: {
  release: AppRelease | null
  submitting: boolean
  onClose: () => void
  onSubmit: (values: CopyValues) => void
}) {
  const isMobile = useIsMobile()
  const [form] = Form.useForm<CopyValues>()
  return (
    <Drawer
      open={!!release}
      onClose={onClose}
      width={isMobile ? '100%' : 620}
      destroyOnHidden
      title={release ? `更新文案 · ${release.version_name}` : '更新文案'}
      footer={
        <DrawerFooter
          pending={submitting}
          submitText="保存文案"
          onCancel={onClose}
          onSubmit={() => form.submit()}
        />
      }
    >
      {release ? (
        <>
          <Alert
            type="warning"
            showIcon
            message="保存后客户端立即读取新文案"
            description={`仅修改 release_notes 与 update_message；制品及投放策略不变。当前 revision：r${release.revision}`}
            style={{ marginBottom: 16 }}
          />
          <Form
            key={`${release.id}:${release.revision}`}
            form={form}
            layout="vertical"
            initialValues={{
              release_notes: release.release_notes,
              update_message: release.update_message ?? '',
            }}
            onFinish={onSubmit}
          >
            <Form.Item
              name="release_notes"
              label="release_notes"
              extra="完整更新内容，支持多行。"
            >
              <Input.TextArea rows={9} />
            </Form.Item>
            <Form.Item
              name="update_message"
              label="update_message（可选）"
              extra="更新弹窗的补充说明。"
            >
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item name="reason" label="审计备注（可选）">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
        </>
      ) : null}
    </Drawer>
  )
}

function DeploymentPolicyDrawer({
  deployment,
  submitting,
  onClose,
  onSubmit,
}: {
  deployment: AppDeployment | null
  submitting: boolean
  onClose: () => void
  onSubmit: (values: DeploymentPolicyValues) => void
}) {
  const isMobile = useIsMobile()
  const [form] = Form.useForm<DeploymentPolicyValues>()
  return (
    <Drawer
      open={!!deployment}
      onClose={onClose}
      width={isMobile ? '100%' : 560}
      destroyOnHidden
      title={
        deployment
          ? `投放策略 · ${deployment.channel_slug} / ${deployment.release.version_name}`
          : '投放策略'
      }
      footer={
        <DrawerFooter
          pending={submitting}
          submitText="保存策略"
          onCancel={onClose}
          onSubmit={() => form.submit()}
        />
      }
    >
      {deployment ? (
        <>
          <Descriptions
            size="small"
            bordered
            column={1}
            items={[
              {
                key: 'release',
                label: '版本',
                children: `${deployment.release.version_name} · code ${deployment.release.version_code}`,
              },
              {
                key: 'revision',
                label: '并发版本',
                children: `r${deployment.revision}`,
              },
            ]}
            style={{ marginBottom: 20 }}
          />
          <Form
            key={`${deployment.id}:${deployment.revision}`}
            form={form}
            layout="vertical"
            initialValues={{
              staged_rollout_percent: deployment.staged_rollout_percent,
              min_supported_code: deployment.min_supported_code,
              reason: '',
            }}
            onFinish={onSubmit}
          >
            <Form.Item
              name="staged_rollout_percent"
              label="灰度比例"
              extra="同一设备按 rollout_seed 稳定命中，不会在刷新时跳组。"
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={100}
                precision={0}
                addonAfter="%"
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name="min_supported_code"
              label="强制更新下限（可选）"
              extra="低于此 code 的客户端会收到 required 更新；不得高于当前候选版本。"
            >
              <InputNumber
                min={1}
                max={deployment.release.version_code}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name="reason"
              label="审计备注"
              rules={[
                { required: true, message: '填写调整原因' },
                { min: 5, message: '备注至少 5 个字' },
              ]}
            >
              <Input.TextArea
                rows={3}
                placeholder="说明灰度或强更策略调整原因"
              />
            </Form.Item>
          </Form>
        </>
      ) : null}
    </Drawer>
  )
}

function CapabilityDrawer({
  policy,
  submitting,
  onClose,
  onSubmit,
}: {
  policy: ClientCapabilityPolicy | null
  submitting: boolean
  onClose: () => void
  onSubmit: (values: CapabilityValues) => void
}) {
  const isMobile = useIsMobile()
  const [form] = Form.useForm<CapabilityValues>()
  return (
    <Drawer
      open={!!policy}
      onClose={onClose}
      width={isMobile ? '100%' : 600}
      destroyOnHidden
      title={policy ? `能力门控 · ${policy.capability}` : '能力门控'}
      footer={
        <DrawerFooter
          pending={submitting}
          submitText="保存策略"
          onCancel={onClose}
          onSubmit={() => form.submit()}
        />
      }
    >
      {policy ? (
        <>
          <Alert
            type="info"
            showIcon
            message="enforce 必须先通过独立预检"
            description="此页允许 disabled / observe / soft。切换 enforce 需要 30 分钟内有效的数据库快照与外部升级、回滚证据，避免误封全部旧客户端。"
            style={{ marginBottom: 16 }}
          />
          <Form
            key={`${policy.capability}:${policy.revision}`}
            form={form}
            layout="vertical"
            initialValues={{
              mode: policy.mode === 'enforce' ? 'observe' : policy.mode,
              soft_min_version_code: policy.soft_min_version_code,
              hard_min_version_code: policy.hard_min_version_code,
              emergency_grace_seconds: policy.emergency_grace_seconds,
              reason: '',
            }}
            onFinish={onSubmit}
          >
            <Form.Item
              name="mode"
              label="策略模式"
              rules={[{ required: true }]}
            >
              <Select
                options={[
                  { value: 'disabled', label: 'disabled · 不执行能力门控' },
                  { value: 'observe', label: 'observe · 只记录判定结果' },
                  { value: 'soft', label: 'soft · 返回升级提示，不硬拒绝' },
                ]}
              />
            </Form.Item>
            <Space.Compact block className="app-update-form-pair">
              <Form.Item
                name="soft_min_version_code"
                label="soft 下限"
                style={{ width: '50%' }}
              >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item
                name="hard_min_version_code"
                label="hard 下限"
                style={{ width: '50%' }}
              >
                <InputNumber min={1} precision={0} style={{ width: '100%' }} />
              </Form.Item>
            </Space.Compact>
            <Form.Item
              name="emergency_grace_seconds"
              label="紧急宽限（秒）"
              extra="仅用于短时故障处置，最大 86400 秒。"
              rules={[{ required: true }]}
            >
              <InputNumber
                min={0}
                max={86400}
                precision={0}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name="reason"
              label="审计备注"
              rules={[
                { required: true, message: '填写调整原因' },
                { min: 5, message: '备注至少 5 个字' },
              ]}
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          </Form>
        </>
      ) : null}
    </Drawer>
  )
}

function BuildKeyDrawer({
  open,
  submitting,
  onClose,
  onSubmit,
}: {
  open: boolean
  submitting: boolean
  onClose: () => void
  onSubmit: (values: BuildKeyValues) => void
}) {
  const isMobile = useIsMobile()
  const [form] = Form.useForm<BuildKeyValues>()
  const platform: BuildKeyPlatform =
    Form.useWatch('platform', form) ?? 'android'
  const copy = BUILD_KEY_PLATFORM_COPY[platform]
  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? '100%' : 620}
      destroyOnHidden
      closable={!submitting}
      keyboard={!submitting}
      maskClosable={!submitting}
      title="签发构建 key"
      footer={
        <DrawerFooter
          pending={submitting}
          submitText="签发 key"
          onCancel={onClose}
          onSubmit={() => form.submit()}
        />
      }
    >
      <Alert
        type="warning"
        showIcon
        message="build secret 为一次性写入材料"
        description="它会随本次请求写入 Vault，成功后不会回显、不会进入列表，也不要在审计备注或 request_id 中填写它。"
        style={{ marginBottom: 16 }}
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={onSubmit}
        requiredMark="optional"
        initialValues={{ platform: 'android' }}
      >
        <Form.Item name="platform" label="平台">
          <Segmented
            block
            options={BUILD_KEY_PLATFORMS.map((value) => ({
              value,
              label: BUILD_KEY_PLATFORM_COPY[value].label,
              icon:
                value === 'windows' ? <WindowsOutlined /> : <AndroidOutlined />,
            }))}
          />
        </Form.Item>
        {platform === 'windows' ? (
          <Alert
            type="info"
            showIcon
            message="Windows build key 只能在这里人工登记"
            description="CI broker 仍只签发 Android。登记后 client-trust-windows 以 key ID 查表，用此处的 app id 与 Authenticode 证书参与 canonical 校验。"
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form.Item
          name="key_id"
          label="key ID"
          dependencies={['platform']}
          extra={`格式 ${platform}-<versionCode>-<channel>-<revision>，例如 ${copy.keyIdExample}。`}
          rules={[
            { required: true, message: '填写 key ID' },
            {
              validator: (_, value?: string) =>
                !value || BUILD_KEY_ID_PATTERNS[platform].test(value.trim())
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        `必须以 ${platform}- 开头，且符合 ${platform}-<versionCode>-<channel>-<revision>（小写字母、数字、连字符）`,
                      ),
                    ),
            },
          ]}
        >
          <Input
            className="app-update-mono"
            placeholder={copy.keyIdExample}
            autoComplete="off"
          />
        </Form.Item>
        <Form.Item
          name="app_id"
          label={copy.appIdLabel}
          extra={copy.appIdExtra}
          rules={[
            { required: true, message: `填写 ${copy.appIdLabel}` },
            {
              pattern: APP_ID_PATTERN,
              message: '必须是反向域名形式，例如 com.example.app',
            },
          ]}
        >
          <Input
            className="app-update-mono"
            placeholder="com.example.app"
            autoComplete="off"
          />
        </Form.Item>
        <Space.Compact block className="app-update-form-pair">
          <Form.Item
            name="version_code"
            label="versionCode"
            style={{ width: '40%' }}
            rules={[{ required: true, message: '填写 versionCode' }]}
          >
            <InputNumber
              min={1}
              max={2147483647}
              precision={0}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="version_name"
            label="versionName"
            style={{ width: '60%' }}
            rules={[{ required: true, message: '填写 versionName' }]}
          >
            <Input className="app-update-mono" autoComplete="off" />
          </Form.Item>
        </Space.Compact>
        <Form.Item
          name="channel"
          label="channel"
          rules={[
            { required: true, message: '填写构建 channel' },
            {
              pattern: CHANNEL_PATTERN,
              message: '小写字母、数字或连字符，最长 32 位',
            },
          ]}
        >
          <Input
            className="app-update-mono"
            placeholder={copy.channelPlaceholder}
            autoComplete="off"
          />
        </Form.Item>
        <Form.Item
          name="signing_cert_sha256"
          label={copy.certLabel}
          extra={copy.certExtra}
          rules={[
            { required: true, message: `填写${copy.certLabel}` },
            {
              pattern: /^[a-fA-F0-9]{64}$/,
              message: '必须是 64 位十六进制 SHA-256',
            },
          ]}
        >
          <Input className="app-update-mono" autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="build_secret"
          label="build secret"
          extra="仅支持粘贴或临时输入；提交完成后立即从表单中销毁。"
          rules={[
            { required: true, message: '填写 build secret' },
            {
              pattern: BUILD_SECRET_PATTERN,
              message: '必须是 64 位十六进制或 43 位 base64url（32 字节）',
            },
          ]}
        >
          <Input.Password
            className="app-update-mono"
            autoComplete="new-password"
          />
        </Form.Item>
        <Space.Compact block className="app-update-form-pair">
          <Form.Item
            name="not_before"
            label="生效时间（可选）"
            style={{ width: '50%' }}
            extra="留空即立即生效。"
          >
            <Input type="datetime-local" />
          </Form.Item>
          <Form.Item
            name="expires_at"
            label="过期时间（可选）"
            style={{ width: '50%' }}
            dependencies={['not_before']}
            extra="留空表示不设过期。"
            rules={[
              ({ getFieldValue }) => ({
                validator: (_, value?: string) => {
                  if (!value || !getFieldValue('not_before'))
                    return Promise.resolve()
                  return new Date(value).getTime() >
                    new Date(getFieldValue('not_before')).getTime()
                    ? Promise.resolve()
                    : Promise.reject(new Error('过期时间必须晚于生效时间'))
                },
              }),
            ]}
          >
            <Input type="datetime-local" />
          </Form.Item>
        </Space.Compact>
        <Form.Item
          name="reason"
          label="审计备注"
          rules={[
            { required: true, message: '填写签发原因' },
            { min: 5, message: '备注至少 5 个字' },
          ]}
        >
          <Input.TextArea
            rows={3}
            placeholder="例如：XF-21 production smoke test"
          />
        </Form.Item>
        <Form.Item
          name="request_id"
          label="request_id（可选）"
          extra="留空时自动生成；用于关联工单或外部执行记录。"
        >
          <Input
            className="app-update-mono"
            placeholder="XF-main-DEV"
            autoComplete="off"
          />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

function DetailDrawer({
  value,
  pending,
  onClose,
  onDeactivate,
}: {
  value: AppDeployment | AppRelease | null
  pending: boolean
  onClose: () => void
  onDeactivate: (deployment: AppDeployment) => void
}) {
  const isMobile = useIsMobile()
  const deployment = value && 'channel_slug' in value ? value : null
  const release = deployment ? deployment.release : (value as AppRelease | null)
  return (
    <Drawer
      open={!!value}
      onClose={onClose}
      width={isMobile ? '100%' : 640}
      title={
        deployment
          ? `部署 #${deployment.id}`
          : release
            ? `${release.version_name} · 版本详情`
            : ''
      }
      footer={
        deployment?.is_active ? (
          <Button
            danger
            icon={<StopOutlined />}
            loading={pending}
            onClick={() => onDeactivate(deployment)}
          >
            停用部署
          </Button>
        ) : (
          <Button onClick={onClose}>关闭</Button>
        )
      }
    >
      {deployment ? (
        <Descriptions
          bordered
          size="small"
          column={1}
          layout={isMobile ? 'vertical' : 'horizontal'}
          title="投放策略"
          items={[
            {
              key: 'channel',
              label: '渠道',
              children: channelTag(
                deployment.channel_slug,
                deployment.channel.assignment_mode,
              ),
            },
            {
              key: 'rollout',
              label: '灰度比例',
              children: `${deployment.staged_rollout_percent}%`,
            },
            {
              key: 'floor',
              label: '强更下限',
              children: deployment.min_supported_code ?? '无',
            },
            {
              key: 'seed',
              label: 'rollout_seed',
              children: (
                <Typography.Text copyable className="app-update-mono">
                  {deployment.rollout_seed}
                </Typography.Text>
              ),
            },
            {
              key: 'state',
              label: '状态',
              children: deploymentState(deployment),
            },
            {
              key: 'published',
              label: '发布时间',
              children: formatCST(deployment.published_at),
            },
            {
              key: 'revision',
              label: 'revision',
              children: `r${deployment.revision}`,
            },
          ]}
          style={{ marginBottom: 20 }}
        />
      ) : null}
      {release ? (
        <Descriptions
          bordered
          size="small"
          column={1}
          layout={isMobile ? 'vertical' : 'horizontal'}
          title="不可变制品"
          items={[
            {
              key: 'version',
              label: '版本',
              children: `${release.version_name} · code ${release.version_code}`,
            },
            { key: 'sdk', label: 'minSdk', children: release.min_sdk ?? '—' },
            {
              key: 'size',
              label: 'APK 大小',
              children: formatBytes(release.apk_size_bytes),
            },
            {
              key: 'sha',
              label: 'SHA-256',
              children: (
                <Typography.Text
                  copyable
                  className="app-update-mono app-update-break"
                >
                  {release.apk_sha256}
                </Typography.Text>
              ),
            },
            {
              key: 'url',
              label: 'APK URL',
              children: (
                <Typography.Text
                  copyable
                  ellipsis
                  className="app-update-mono"
                  style={{ maxWidth: isMobile ? '100%' : 430 }}
                >
                  {release.apk_url}
                </Typography.Text>
              ),
            },
            {
              key: 'first',
              label: '首次发布',
              children: formatCST(release.first_published_at),
            },
            {
              key: 'revision',
              label: 'revision',
              children: `r${release.revision}`,
            },
          ]}
        />
      ) : null}
    </Drawer>
  )
}

function ChannelDrawer({
  open,
  pending,
  onClose,
  onSubmit,
}: {
  open: boolean
  pending: boolean
  onClose: () => void
  onSubmit: (values: { slug: string; priority: number; reason: string }) => void
}) {
  const isMobile = useIsMobile()
  const [form] = Form.useForm()
  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? '100%' : 500}
      destroyOnHidden
      title="新建设备渠道"
      footer={
        <DrawerFooter
          pending={pending}
          submitText="创建渠道"
          onCancel={onClose}
          onSubmit={() => form.submit()}
        />
      }
    >
      <Alert
        type="warning"
        showIcon
        message="stable 与 beta 不能在这里创建或改为设备渠道"
        description="设备渠道用于少量定向验证，优先级越大越先匹配。"
        style={{ marginBottom: 16 }}
      />
      <Form
        form={form}
        layout="vertical"
        initialValues={{ priority: 100 }}
        onFinish={onSubmit}
      >
        <Form.Item
          name="slug"
          label="渠道 slug"
          rules={[
            { required: true },
            {
              pattern: /^[a-z0-9][a-z0-9-]{0,47}[a-z0-9]$/,
              message: '使用小写字母、数字与连字符，长度 2–49',
            },
          ]}
        >
          <Input className="app-update-mono" placeholder="super-dev" />
        </Form.Item>
        <Form.Item name="priority" label="优先级" rules={[{ required: true }]}>
          <InputNumber
            min={0}
            max={1000}
            precision={0}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name="reason"
          label="审计备注"
          rules={[{ required: true }, { min: 5, message: '备注至少 5 个字' }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

function MemberDrawer({
  open,
  pending,
  channels,
  onClose,
  onSubmit,
}: {
  open: boolean
  pending: boolean
  channels: AppChannel[]
  onClose: () => void
  onSubmit: (values: {
    device_id: string
    channel_slug: string
    note?: string
    reason: string
  }) => void
}) {
  const isMobile = useIsMobile()
  const [form] = Form.useForm()
  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? '100%' : 520}
      destroyOnHidden
      title="添加定向设备"
      footer={
        <DrawerFooter
          pending={pending}
          submitText="保存定向"
          onCancel={onClose}
          onSubmit={() => form.submit()}
        />
      }
    >
      <Alert
        type="info"
        showIcon
        message="设备 ID 仅用于服务端哈希"
        description="明文不会保存，也不会从列表接口返回。"
        style={{ marginBottom: 16 }}
      />
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Form.Item
          name="device_id"
          label="设备 ID"
          rules={[{ required: true }]}
        >
          <Input className="app-update-mono" />
        </Form.Item>
        <Form.Item
          name="channel_slug"
          label="目标渠道"
          rules={[{ required: true }]}
        >
          <Select
            options={channels.map((item) => ({
              value: item.slug,
              label: item.slug,
            }))}
          />
        </Form.Item>
        <Form.Item name="note" label="设备备注">
          <Input placeholder="例如：Pixel 9 测试机" />
        </Form.Item>
        <Form.Item
          name="reason"
          label="审计备注"
          rules={[{ required: true }, { min: 5, message: '备注至少 5 个字' }]}
        >
          <Input.TextArea rows={3} />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

function ReasonModal({
  open,
  title,
  description,
  confirmText,
  danger,
  pending,
  onClose,
  onSubmit,
}: {
  open: boolean
  title: string
  description: string
  confirmText: string
  danger?: boolean
  pending: boolean
  onClose: () => void
  onSubmit: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      okText={confirmText}
      okButtonProps={{ danger, disabled: reason.trim().length < 5 }}
      confirmLoading={pending}
      onOk={() => onSubmit(reason.trim())}
      destroyOnHidden
      afterClose={() => setReason('')}
    >
      <Alert
        type={danger ? 'error' : 'warning'}
        showIcon
        message={description}
        style={{ marginBottom: 16 }}
      />
      <Typography.Text strong>审计备注</Typography.Text>
      <Input.TextArea
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        rows={3}
        placeholder="至少 5 个字，说明操作原因"
        style={{ marginTop: 8 }}
      />
    </Modal>
  )
}

/** 手机卡片：整卡可点开详情 / 编辑；无 onOpen 时只是信息卡 */
function MobileRecord({
  title,
  status,
  onOpen,
  actions,
  children,
}: {
  title: ReactNode
  status?: ReactNode
  onOpen?: () => void
  actions?: AdminRecordAction[]
  children?: ReactNode
}) {
  const interactive = onOpen
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: onOpen,
        onKeyDown: (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') onOpen()
        },
      }
    : {}
  return (
    <article
      className={
        onOpen
          ? 'admin-mobile-record'
          : 'admin-mobile-record app-update-record--static'
      }
      {...interactive}
    >
      <div className="admin-mobile-record__head">
        <div className="admin-mobile-record__title">{title}</div>
        {status}
      </div>
      {children}
      {actions ? <AdminRecordActions actions={actions} /> : null}
    </article>
  )
}

function pageSlice<T>(rows: T[], page: number) {
  return rows.slice((page - 1) * MOBILE_PAGE_SIZE, page * MOBILE_PAGE_SIZE)
}

function DrawerFooter({
  pending,
  submitText,
  onCancel,
  onSubmit,
}: {
  pending: boolean
  submitText: string
  onCancel: () => void
  onSubmit: () => void
}) {
  return (
    <Space style={{ float: 'right' }}>
      <Button onClick={onCancel}>取消</Button>
      <Button type="primary" loading={pending} onClick={onSubmit}>
        {submitText}
      </Button>
    </Space>
  )
}

function channelTag(slug: string, mode: AppChannel['assignment_mode']) {
  const color =
    slug === 'stable' ? 'green' : slug === 'beta' ? 'blue' : 'purple'
  return (
    <Tag color={color}>
      {slug}
      {mode === 'device' ? ' · 定向' : ''}
    </Tag>
  )
}

function deploymentState(row: AppDeployment) {
  if (row.is_active) return <Tag color="success">生效中</Tag>
  if (row.published_at) return <Tag>已下线</Tag>
  return <Tag color="warning">草稿</Tag>
}

function capabilityModeTag(mode: CapabilityMode) {
  const color =
    mode === 'enforce'
      ? 'red'
      : mode === 'soft'
        ? 'gold'
        : mode === 'observe'
          ? 'blue'
          : 'default'
  return <Tag color={color}>{modeLabel(mode)}</Tag>
}

function modeLabel(mode: CapabilityMode) {
  return (
    {
      disabled: '已关闭',
      observe: '观察',
      soft: '软门控',
      enforce: '强制执行',
    } as const
  )[mode]
}

function buildKeyStatusTag(key: ClientBuildKey) {
  if (key.status === 'revoked') return <Tag color="error">已撤销</Tag>
  if (key.status === 'retired') return <Tag>已退役</Tag>
  if (key.expires_at && new Date(key.expires_at).getTime() <= Date.now())
    return <Tag color="warning">已过期</Tag>
  return <Tag color="success">活动</Tag>
}

function buildKeyPlatformTag(key: ClientBuildKey) {
  const platform = buildKeyPlatform(key.key_id)
  if (platform === 'windows')
    return (
      <Tag color="blue" icon={<WindowsOutlined />}>
        Windows
      </Tag>
    )
  if (platform === 'android')
    return (
      <Tag color="green" icon={<AndroidOutlined />}>
        Android
      </Tag>
    )
  return <Tag>未知平台</Tag>
}

function auditActionLabel(action: string) {
  return (
    (
      {
        'build_key.register': '注册临时构建密钥',
        'build_key.commit_release': '原子提交版本与部署',
        'build_key.commit_release_observability': '上传可观测性制品',
        'build_key.revoke': '撤销构建密钥',
      } as Record<string, string>
    )[action] ?? action
  )
}

function formatBytes(value: number | null | undefined) {
  if (!value) return '—'
  return `${(value / 1024 / 1024).toFixed(1)} MB`
}

async function adminRpc(name: string, params: JsonRecord = {}) {
  const { data, error } = await (
    supabase as never as {
      schema: (schema: string) => {
        rpc: (
          fn: string,
          args: JsonRecord,
        ) => Promise<{ data: unknown; error: { message: string } | null }>
      }
    }
  )
    .schema('admin')
    .rpc(name, compact(params))
  if (error) throw new Error(error.message)
  return data as RpcResult & JsonRecord
}

async function adminOk(name: string, params: JsonRecord = {}) {
  return expectOk(await adminRpc(name, params))
}

async function adminList<T>(
  name: string,
  params: JsonRecord = {},
): Promise<ListResult<T>> {
  const result = await adminRpc(name, {
    p_limit: PAGE_SIZE,
    p_offset: 0,
    ...params,
  })
  expectOk(result)
  return result as ListResult<T>
}

async function adminRows<T>(name: string): Promise<ListResult<T>> {
  const result = await adminRpc(name)
  expectOk(result)
  const rows = (result.rows as T[] | undefined) ?? []
  return { ...result, rows, total: rows.length }
}

function expectOk(result: RpcResult) {
  if (!result.ok) throw new Error(result.error ?? 'unknown')
  return result
}

function compact(value: JsonRecord) {
  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => item !== undefined),
  )
}

function withoutBuildSecret(values: BuildKeyValues): BuildKeySubmission {
  return {
    key_id: values.key_id,
    app_id: values.app_id,
    version_code: values.version_code,
    version_name: values.version_name,
    channel: values.channel,
    signing_cert_sha256: values.signing_cert_sha256,
    not_before: values.not_before,
    expires_at: values.expires_at,
    reason: values.reason,
    request_id: values.request_id,
  }
}

function toOptionalIsoString(localValue?: string) {
  if (!localValue) return undefined
  const date = new Date(localValue)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

function createBuildKeyRequestId(action = 'register') {
  const stamp = new Date().toISOString().replace(/[-:.]/g, '')
  const suffix = globalThis.crypto.randomUUID().slice(0, 8)
  return `admin-build-key-${action}-${stamp}-${suffix}`
}

function formatRpcError(error: string) {
  if (error.includes('ANDROID_ENFORCE_PREFLIGHT_REQUIRED')) {
    return '缺少 30 分钟内有效且与当前 revision、hard minimum 匹配的预检 PASS，请重新预检'
  }
  if (error.includes('ANDROID_ENFORCE_PREFLIGHT_STALE')) {
    return '预检后的 deployment、release 或 build key 已变化，请刷新后重新预检'
  }
  const labels: Record<string, string> = {
    revision_conflict: '数据已被其他操作修改，请刷新后重试',
    immutable_release: '已发布版本的制品字段不可修改',
    immutable_deployment: '已发布部署不能更换渠道或版本',
    invalid_policy: '策略参数无效，请检查版本下限与灰度比例',
    invalid_preflight:
      '预检参数无效，请检查 applicationId、版本、SDK 与证据引用',
    invalid_channel: '渠道配置无效',
    invalid_build_key:
      '构建 key 参数无效：检查 key ID 格式、app id、channel、证书 SHA-256、secret 与有效窗口',
    build_key_exists: '该 key ID 已存在，请换一个 revision',
    forbidden: '当前账号没有管理员权限',
  }
  return labels[error] ?? error
}
